import React, { useState, useEffect, Suspense } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DroppableProvided } from 'react-beautiful-dnd';
import { Upload, Search, Edit2, Trash2, Plus, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { MenuService } from '../services/menuService';
import { MenuItemWithTranslations, Language, Category } from '../types/menu';
import CategoryManagement from '../components/CategoryManagement';
import Notification from '../components/common/Notification';
import { optimizeAndUploadImage } from '../services/imageService';
import { trackMenuItemInteraction, trackCategoryView } from '../services/analyticsService';
import ProgressiveImage from '../components/common/ProgressiveImage';
import ErrorBoundary from '../components/ErrorBoundary';
import { checkTranslationQuality } from '../services/translationQuality';
import { debounce } from 'lodash';
import { pizzaCategories } from '../data/menuData';

const AdminTranslationEditor = React.lazy(() => import('../components/admin/AdminTranslationEditor'));

const SUPPORTED_LANGUAGES: Language[] = ['fr', 'ar', 'en'];

interface NotificationState {
  message: string;
  type: 'success' | 'error' | 'info';
}

const MenuManagement: React.FC = () => {
  const [items, setItems] = useState<MenuItemWithTranslations[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [editingItem, setEditingItem] = useState<MenuItemWithTranslations | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [activeTab, setActiveTab] = useState<'items' | 'categories'>('items');
  const [activeTranslation, setActiveTranslation] = useState<{
    field: 'name' | 'description';
    sourceLang: Language;
    targetLang: Language;
  } | null>(null);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  const createEmptyItem = (): MenuItemWithTranslations => ({
    id: `item_${Date.now()}`,
    price: 0,
    category: categories[0]?.id || '',
    icon: "🍽️",
    translations: {
      fr: { name: '', description: '' },
      ar: { name: '', description: '' },
      en: { name: '', description: '' }
    },
    order: items.length
  });

  /** Display name for an item's category (items store the category ID). */
  const categoryNameOf = (id: string): string =>
    categories.find(c => c.id === id)?.name ?? id;

  const openEditor = (item: MenuItemWithTranslations, isNew: boolean) => {
    setImagePreview(null);
    setIsNewItem(isNew);
    setEditingItem(item);
  };

  const closeEditor = () => {
    setImagePreview(null);
    setIsNewItem(false);
    setEditingItem(null);
  };

  // Image handling functions
  const handleImageUpload = async (file: File) => {
    try {
      setUploading(true);
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image size should be less than 5MB');
      }

      const result = await optimizeAndUploadImage(file);
      
      if (editingItem) {
        setEditingItem({
          ...editingItem,
          image: result.url
        });
      }

      setImagePreview(result.url);
      showNotification('Image uploaded successfully', 'success');
    } catch (error: unknown) {
      console.error('Image upload error:', error);
      if (error instanceof Error) {
        showNotification(error.message, 'error');
      } else {
        showNotification('Failed to upload image', 'error');
      }
    } finally {
      setUploading(false);
    }
  };

  // Render item card
  const renderItemCard = (item: MenuItemWithTranslations) => (
    <div className="bg-white rounded-2xl border border-stone-200/70 shadow-sm hover:shadow-md transition-shadow p-3">
      <div className="flex gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base text-stone-800 truncate">{item.translations.fr.name}</h3>
          <p className="text-sm font-semibold text-amber-600 mt-0.5">{item.price} DH</p>
          <span className="inline-block mt-1.5 px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full text-xs">
            {categoryNameOf(item.category)}
          </span>
          <div className="flex gap-1 mt-2">
            <button 
              onClick={() => handleItemClick(item)}
              aria-label={`Edit ${item.translations.fr.name || 'item'}`}
              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg"
            >
              <Edit2 size={15} aria-hidden="true" />
            </button>
            <button 
              onClick={() => handleDeleteItem(item.id)}
              aria-label={`Delete ${item.translations.fr.name || 'item'}`}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
            >
              <Trash2 size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
        {item.image && (
          <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-white">
            <ProgressiveImage
              src={item.image}
              alt={item.translations.fr.name}
              className="w-full h-full"
              size="thumbnail"
              blur
            />
          </div>
        )}
      </div>
    </div>
  );

  // Add this helper function to sanitize IDs
  const sanitizeId = (id: string): string => {
    return id.replace(/[^a-zA-Z0-9-_]/g, '_');
  };

  // Render draggable item card
  const renderDraggableItemCard = (item: MenuItemWithTranslations, index: number) => {
    const draggableId = `menu-item-${sanitizeId(item.id)}`;
    
    return (
      <Draggable 
        key={draggableId}
        draggableId={draggableId}
        index={index}
      >
        {(provided: DraggableProvided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className="mb-4"
          >
            {renderItemCard(item)}
          </div>
        )}
      </Draggable>
    );
  };

  // Edit modal with type-safe language handling
  const renderEditModal = () => {
    if (!editingItem) return null;
    const previewSrc = imagePreview || editingItem.image;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-3 sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-item-title"
          className="bg-white rounded-2xl p-4 sm:p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 id="edit-item-title" className="text-lg sm:text-xl font-bold text-stone-800">
              {isNewItem ? 'Add New Item' : 'Edit Item'}
            </h2>
            <button
              onClick={closeEditor}
              aria-label="Close editor"
              className="p-2 hover:bg-stone-100 rounded-lg text-stone-500"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          {/* Image upload section */}
          <div className="mb-4">
            <div className="flex items-center gap-3">
              {previewSrc && (
                <div className="w-20 h-20 rounded-xl overflow-hidden">
                  <ProgressiveImage
                    src={previewSrc}
                    alt="Preview"
                    className="w-full h-full"
                    size="thumbnail"
                    blur
                  />
                </div>
              )}
              <label className="flex items-center gap-2 px-3.5 py-1.5 bg-stone-100 rounded-lg cursor-pointer hover:bg-stone-200 text-sm text-stone-700">
                <Upload size={16} aria-hidden="true" />
                <span>{uploading ? 'Uploading…' : 'Upload Image'}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                    // Allow re-selecting the same file later
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          </div>

          {/* Multilingual form with type safety */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <div key={lang} className="space-y-3">
                <h3 className="font-semibold text-xs uppercase tracking-wide text-stone-500">{lang}</h3>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Name"
                    aria-label={`Name (${lang})`}
                    value={editingItem.translations[lang].name}
                    onChange={(e) => {
                      setEditingItem({
                        ...editingItem,
                        translations: {
                          ...editingItem.translations,
                          [lang]: {
                            ...editingItem.translations[lang],
                            name: e.target.value
                          }
                        }
                      });
                    }}
                    className="w-full p-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
                  />
                  {lang !== 'fr' && (
                    <button
                      onClick={() => setActiveTranslation({
                        field: 'name',
                        sourceLang: 'fr',
                        targetLang: lang
                      })}
                      aria-label={`Translate name to ${lang}`}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-stone-100 rounded text-stone-400"
                    >
                      <Edit2 size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
                <div className="relative">
                  <textarea
                    placeholder="Description"
                    aria-label={`Description (${lang})`}
                    value={editingItem.translations[lang].description}
                    onChange={(e) => {
                      setEditingItem({
                        ...editingItem,
                        translations: {
                          ...editingItem.translations,
                          [lang]: {
                            ...editingItem.translations[lang],
                            description: e.target.value
                          }
                        }
                      });
                    }}
                    className="w-full p-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
                    rows={2}
                  />
                  {lang !== 'fr' && (
                    <button
                      onClick={() => setActiveTranslation({
                        field: 'description',
                        sourceLang: 'fr',
                        targetLang: lang
                      })}
                      aria-label={`Translate description to ${lang}`}
                      className="absolute right-2 top-2 p-1 hover:bg-stone-100 rounded text-stone-400"
                    >
                      <Edit2 size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Additional fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div>
              <label htmlFor="item-price" className="block text-sm font-medium mb-1 text-stone-600">Price (DH)</label>
              <input
                id="item-price"
                type="number"
                value={editingItem.price}
                onChange={(e) => setEditingItem({
                  ...editingItem,
                  price: Number(e.target.value)
                })}
                className="w-full p-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
              />
            </div>
            <div>
              <label htmlFor="item-category" className="block text-sm font-medium mb-1">Category</label>
              <select
                id="item-category"
                value={editingItem.category}
                onChange={(e) => setEditingItem((prev) => {
                  if (!prev) return prev;
                  const next = { ...prev, category: e.target.value };
                  if (e.target.value === 'pizzas') {
                    // Default pizza subcategory when switching to Pizzas.
                    next.subcategory = 'incontournables';
                  } else {
                    // Non-pizza items have no subcategory. Drop the key instead
                    // of leaving `undefined` — Firebase's set() rejects any
                    // payload containing undefined values.
                    delete next.subcategory;
                  }
                  return next;
                })}
                className="w-full p-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            {editingItem.category === 'pizzas' && (
              <div>
                <label htmlFor="item-subcategory" className="block text-sm font-medium mb-1">Subcategory</label>
                <select
                  id="item-subcategory"
                  value={editingItem.subcategory || 'incontournables'}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    subcategory: e.target.value
                  })}
                  className="w-full p-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
                >
                  {Object.keys(pizzaCategories).map((key) => (
                    <option key={key} value={key}>{t(`menu.categories.pizza.${key}`)}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="item-icon" className="block text-sm font-medium mb-1">Icon</label>
              <input
                id="item-icon"
                type="text"
                value={editingItem.icon}
                onChange={(e) => setEditingItem({
                  ...editingItem,
                  icon: e.target.value
                })}
                className="w-full p-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={closeEditor}
              className="px-4 py-2 text-sm text-stone-600 hover:bg-stone-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveItem}
              className="px-4 py-2 bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white rounded-lg shadow-sm hover:shadow-md text-sm font-medium"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleDeleteItem = async (id: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    const item = items.find(i => i.id === id);
    try {
      await MenuService.deleteMenuItem(id, item?.image);
      setItems(prev => prev.filter(i => i.id !== id));
      showNotification('Item deleted', 'success');
    } catch (err) {
      console.error('Failed to delete menu item:', err);
      showNotification('Failed to delete item', 'error');
    }
  };

  const handleSaveItem = async (): Promise<void> => {
    if (!editingItem) return;

    try {
      await MenuService.saveMenuItem(editingItem);
      setItems(prev => {
        const index = prev.findIndex(i => i.id === editingItem.id);
        if (index >= 0) {
          return [...prev.slice(0, index), editingItem, ...prev.slice(index + 1)];
        }
        return [...prev, editingItem];
      });
      closeEditor();
      showNotification('Item saved successfully', 'success');
    } catch (err) {
      showNotification('Failed to save item', 'error');
      console.error('Failed to save menu item:', err);
    }
  };

  // Add debounce to drag end handler (useMemo so the memoized fn ref is stable
  // and the previous useCallback form didn't trip react-hooks/exhaustive-deps)
  const debouncedUpdateOrder = React.useMemo(
    () =>
      debounce((items: MenuItemWithTranslations[]) => {
        MenuService.updateItemOrder(items).catch((err: unknown) => {
          console.error('Failed to persist item order:', err);
        });
      }, 500),
    []
  );

  // Cancel any pending order update when the component unmounts
  useEffect(() => {
    return () => debouncedUpdateOrder.cancel();
  }, [debouncedUpdateOrder]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    // Drag indices refer to the rendered (filtered + paginated) list,
    // so map them back onto the full items array by item identity.
    const visibleList = [...paginatedItems];
    const [movedItem] = visibleList.splice(result.source.index, 1);
    const targetItem = visibleList[result.destination.index];
    const lastVisibleItem = visibleList[visibleList.length - 1];

    const nextItems = [...items];
    const fromIndex = nextItems.findIndex(i => i.id === movedItem.id);
    if (fromIndex === -1) return;
    nextItems.splice(fromIndex, 1);

    let toIndex = nextItems.length;
    if (targetItem) {
      const targetIndex = nextItems.findIndex(i => i.id === targetItem.id);
      if (targetIndex !== -1) toIndex = targetIndex;
    } else if (lastVisibleItem) {
      const lastVisibleIndex = nextItems.findIndex(i => i.id === lastVisibleItem.id);
      if (lastVisibleIndex !== -1) toIndex = lastVisibleIndex + 1;
    }
    nextItems.splice(toIndex, 0, movedItem);

    const updatedItems = nextItems.map((item, index) => ({
      ...item,
      order: index
    }));

    setItems(updatedItems);
    debouncedUpdateOrder(updatedItems);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      // Load data in parallel
      const [categoryList, menuItems] = await Promise.all([
        MenuService.getCategories(),
        MenuService.getMenuItems()
      ]);
      
      setCategories(categoryList);
      setItems(menuItems);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoadError('Failed to load menu data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCategory !== 'all') {
      trackCategoryView(selectedCategory);
    }
  }, [selectedCategory]);

  const handleItemClick = (item: MenuItemWithTranslations) => {
    trackMenuItemInteraction({ item, action: 'view_details' });
    openEditor(item, false);
  };

  const handleTranslationSave = (text: string) => {
    if (!editingItem || !activeTranslation) return;
    
    const quality = checkTranslationQuality(text, activeTranslation.targetLang, activeTranslation.field);
    
    if (quality.score < 70) {
      if (!window.confirm('Translation quality is low. Save anyway?')) {
        return;
      }
    }

    setEditingItem({
      ...editingItem,
      translations: {
        ...editingItem.translations,
        [activeTranslation.targetLang]: {
          ...editingItem.translations[activeTranslation.targetLang],
          [activeTranslation.field]: text
        }
      }
    });

    setActiveTranslation(null);
  };

  // Reset to page 1 whenever the filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedCategory]);

  // Memoize the filtered items calculation
  const filteredItems = React.useMemo(() => 
    items.filter(item => 
      (selectedCategory === 'all' || item.category === selectedCategory) &&
      (searchQuery === '' || 
        Object.values(item.translations).some(trans => 
          trans.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          trans.description?.toLowerCase().includes(searchQuery.toLowerCase())
        ))
    ),
    [items, selectedCategory, searchQuery]
  );

  // Clamp the current page when the filtered list shrinks (e.g. after a delete)
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [filteredItems.length, page]);

  // Update items rendering
  const paginatedItems = React.useMemo(() => 
    filteredItems.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filteredItems, page]
  );

  const ItemSkeleton = () => (
    <div className="bg-white rounded-2xl border border-stone-200/70 shadow-sm p-3 animate-pulse">
      <div className="flex gap-3">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-stone-200 rounded w-2/3" />
          <div className="h-3 bg-stone-200 rounded w-1/3" />
          <div className="h-3 bg-stone-200 rounded w-1/4" />
        </div>
        <div className="w-20 h-20 bg-stone-200 rounded-xl" />
      </div>
    </div>
  );

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Tabs */}
          <div className="flex gap-2 mb-4 flex-wrap" role="group" aria-label="Menu management views">
            <button
              onClick={() => setActiveTab('items')}
              aria-pressed={activeTab === 'items'}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === 'items'
                  ? 'bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white shadow-sm'
                  : 'bg-white text-stone-600 border border-stone-200 hover:border-amber-300'
              }`}
            >
              Menu Items
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              aria-pressed={activeTab === 'categories'}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === 'categories'
                  ? 'bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white shadow-sm'
                  : 'bg-white text-stone-600 border border-stone-200 hover:border-amber-300'
              }`}
            >
              Categories
            </button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <ItemSkeleton key={i} />
              ))}
            </div>
          ) : loadError ? (
            <div role="alert" className="text-center py-8">
              <p className="text-red-600 mb-4">{loadError}</p>
              <button
                onClick={loadData}
                className="px-4 py-2 bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white rounded-lg shadow-sm text-sm font-medium"
              >
                Retry
              </button>
            </div>
          ) : activeTab === 'categories' ? (
            // Categories Management
            <CategoryManagement 
              categories={categories}
              onCategoryChange={setCategories}
            />
          ) : (
            // Menu Items Management
            <>
              <div className="flex justify-end mb-3">
                <button
                  onClick={() => openEditor(createEmptyItem(), true)}
                  className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white rounded-lg shadow-sm hover:shadow-md text-sm font-medium"
                >
                  <Plus size={18} aria-hidden="true" />
                  Add Item
                </button>
              </div>

              {/* Search and filters */}
              <div className="mb-5">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={17} aria-hidden="true" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      aria-label="Search menu items"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
                    />
                  </div>
                  <select
                    aria-label="Filter by category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Items grid */}
              {filteredItems.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-stone-500 text-sm">No menu items found.</p>
                  <button
                    onClick={() => openEditor(createEmptyItem(), true)}
                    className="mt-4 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white rounded-lg shadow-sm text-sm font-medium mx-auto"
                  >
                    <Plus size={18} aria-hidden="true" />
                    Add First Item
                  </button>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="menu-items-list">
                    {(provided: DroppableProvided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4"
                      >
                        {paginatedItems.map((item, index) => renderDraggableItemCard(item, index))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}

              {/* Add pagination controls */}
              {Math.ceil(filteredItems.length / ITEMS_PER_PAGE) > 1 && (
                <nav aria-label="Pagination" className="mt-4 flex justify-center gap-1.5">
                  {Array.from({ length: Math.ceil(filteredItems.length / ITEMS_PER_PAGE) }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      aria-label={`Go to page ${i + 1}`}
                      aria-current={page === i + 1 ? 'page' : undefined}
                      className={`w-8 h-8 rounded-lg text-sm ${page === i + 1 ? 'bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white shadow-sm' : 'bg-white text-stone-600 border border-stone-200 hover:border-amber-300'}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </nav>
              )}
            </>
          )}
        </div>

        {/* Edit Modal */}
        {editingItem && renderEditModal()}

        {/* Notification */}
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        {/* TranslationEditor modal */}
        {activeTranslation && editingItem && (
          <Suspense fallback={<div role="status">Loading editor…</div>}>
            <AdminTranslationEditor
              mode="item"
              sourceLang={activeTranslation.sourceLang}
              targetLang={activeTranslation.targetLang}
              sourceText={editingItem.translations[activeTranslation.sourceLang][activeTranslation.field]}
              targetText={editingItem.translations[activeTranslation.targetLang][activeTranslation.field]}
              onItemSave={handleTranslationSave}
              onClose={() => setActiveTranslation(null)}
              context={activeTranslation.field}
            />
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default MenuManagement; 